/* eslint-disable */

import { LocaleCode } from '~/common/interfaces';
import {
    PluralTranslation,
    TranslationKey,
    TranslationOptions,
    TranslationParams,
    Translations,
    TranslationValue,
} from '~/modules/utils/i18n';

export class TranslationService extends EventTarget {
    private static instance: TranslationService | undefined;
    private translations: Map<LocaleCode, Translations> = new Map();
    private currentLocale: LocaleCode = LocaleCode.DE_CH;
    private fallbackLocale: LocaleCode = LocaleCode.DE_CH;
    private pluralRules: Map<LocaleCode, Intl.PluralRules> = new Map();
    private initialized = false;
    private supportedLocales: LocaleCode[] = [LocaleCode.DE_CH, LocaleCode.FR_CH];

    constructor() {
        super();
        this.initialize();
    }

    static getInstance(): TranslationService {
        if (!TranslationService.instance) {
            TranslationService.instance = new TranslationService();
        }
        return TranslationService.instance;
    }

    // Reset the singleton instance (for testing purposes only)
    static resetInstance(): void {
        TranslationService.instance = undefined;
    }

    private initialize(): void {
        if (this.initialized) return;

        // Initialize empty translation maps for each locale
        this.supportedLocales.forEach((locale) => {
            this.translations.set(locale, {});
            // Initialize plural rules for each locale
            this.pluralRules.set(locale, new Intl.PluralRules(locale));
        });

        this.initialized = true;
    }

    // Add translations for a specific locale
    addTranslations(locale: LocaleCode, translations: Translations): void {
        if (!this.initialized) {
            this.initialize();
        }

        const existingTranslations = this.translations.get(locale) || {};
        this.translations.set(locale, {
            ...existingTranslations,
            ...translations,
        });

        this.currentLocale = locale;

        this.dispatchEvent(
            new CustomEvent('der.translations-added', {
                detail: { locale },
            })
        );
    }

    private isSupportedLocale(locale: LocaleCode): locale is LocaleCode {
        return this.supportedLocales.includes(locale);
    }

    async setLocale(locale: LocaleCode): Promise<void> {
        if (!this.initialized) {
            this.initialize();
        }

        if (this.isSupportedLocale(locale)) {
            this.currentLocale = locale;
        } else {
            console.warn(`Locale ${locale} not available`);
        }
    }

    // Get translation value (string or plural object)
    private getTranslationValue(key: TranslationKey): TranslationValue | undefined {
        let value = this.translations.get(this.currentLocale)?.[key];

        if (value === undefined && this.currentLocale !== this.fallbackLocale) {
            value = this.translations.get(this.fallbackLocale)?.[key];
        }

        return value;
    }

    /**
     * Main translation method with % placeholder support
     */
    translate(key: TranslationKey, params?: TranslationParams, fallback?: string): string {
        if (!this.initialized) {
            this.initialize();
        }

        const value = this.getTranslationValue(key);

        let translation: string;

        if (value === undefined) {
            console.warn(`Missing translation for key: ${key} in locale: ${this.currentLocale}`);
            translation = fallback ?? key;
        } else if (typeof value === 'string') {
            translation = value;
        } else {
            // It's a plural object, but we're not using plural - use "other" or first available
            translation = value['other'] || value['#1'] || Object.values(value)[0] || key;
        }

        // Replace parameters with % syntax
        if (params) {
            translation = this.replacePercentParams(translation, params);
        }

        return translation;
    }

    // Translation method with options object (alternative syntax)
    translateWithOptions(key: TranslationKey, options: TranslationOptions = {}): string {
        const { params, fallback } = options;
        return this.translate(key, params, fallback);
    }

    // Plural translation using object lookup
    translatePlural(key: TranslationKey, count: number, params?: TranslationParams, fallback?: string): string {
        if (!this.initialized) {
            this.initialize();
        }

        const value = this.getTranslationValue(key);

        let translation: string;

        if (value === undefined) {
            console.warn(`Missing translation for key: ${key} in locale: ${this.currentLocale}`);
            translation = fallback || key;
        } else if (typeof value === 'string') {
            // Not a plural translation, use as is
            translation = value;
        } else {
            // It's a plural object - use optimized lookup
            translation = this.selectPluralForm(value, count, this.currentLocale);
        }

        // Replace # with count
        translation = translation.replace(/#/g, count.toString());

        // Always replace %count% with the count value
        translation = this.replacePercentParams(translation, { count });

        // Replace other parameters with % syntax
        if (params) {
            translation = this.replacePercentParams(translation, params);
        }

        return translation;
    }

    // Replace parameters using % syntax
    // Example: %name% -> John, %count% -> 5
    private replacePercentParams(text: string, params: TranslationParams): string {
        let result = text;

        Object.entries(params).forEach(([key, value]) => {
            const regex1 = new RegExp(`%${key}%`, 'g');

            result = result.replace(regex1, String(value));
        });

        return result;
    }

    // Plural form selection
    private selectPluralForm(pluralObj: PluralTranslation, count: number, locale: LocaleCode): string {
        // 1. Check for exact match (e.g., "#0", "#1", "#2")
        const exactKey = `#${count}`;
        if (pluralObj[exactKey]) {
            return pluralObj[exactKey];
        }

        // 2. Use Intl.PluralRules for category selection (only if needed)
        const pluralRules = this.pluralRules.get(locale);
        if (pluralRules) {
            const category = pluralRules.select(count);
            if (pluralObj[category]) {
                return pluralObj[category];
            }
        }

        // 3. Fallback to "other"
        if (pluralObj['other']) {
            return pluralObj['other'];
        }

        // 4. Last resort: return first available value
        const firstValue = Object.values(pluralObj)[0];
        return firstValue || `${count}`;
    }

    getLocale(): LocaleCode {
        return this.currentLocale;
    }

    getAvailableLocales(): LocaleCode[] {
        return Array.from(this.translations.keys());
    }

    hasTranslation(key: TranslationKey, locale?: LocaleCode): boolean {
        const checkLocale = locale || this.currentLocale;
        return !!this.translations.get(checkLocale)?.[key];
    }
}
